export type OrganizationSettingsTab = 'basic' | 'public-site' | 'sports' | 'player-ratings';
export type AcademySettingsTab = 'general' | 'advanced';

const ORGANIZATION_TABS = new Set<string>(['basic', 'public-site', 'sports', 'player-ratings']);
const ACADEMY_TABS = new Set<string>(['general', 'advanced', 'lessons', 'age-groups', 'lesson-settings']);

/** Legacy club settings tabs now live on feature management pages. */
const LEGACY_CLUB_TAB_MAP: Record<string, string> = {
  memberships: '/admin/members?tab=settings',
  'court-hire': '/admin/court-hire?tab=settings',
  hours: '/admin/courts?tab=settings',
  'academy-access': '/admin/courts?tab=settings',
};

const LEGACY_ORGANIZATION_TAB_MAP: Record<string, OrganizationSettingsTab> = {
  customization: 'public-site',
  subdomain: 'public-site',
  'website-embed': 'public-site',
  branding: 'public-site',
};

const LEGACY_ACADEMY_TAB_MAP: Record<string, AcademySettingsTab> = {
  lessons: 'advanced',
  'lesson-settings': 'advanced',
  'age-groups': 'general',
};

/** Maps any legacy settings tab to the correct new settings URL. */
export function resolveSettingsPath(tab: string | null): string {
  if (!tab) {
    return '/admin/organization/settings';
  }

  if (LEGACY_ORGANIZATION_TAB_MAP[tab]) {
    return `/admin/organization/settings?tab=${LEGACY_ORGANIZATION_TAB_MAP[tab]}`;
  }

  if (LEGACY_ACADEMY_TAB_MAP[tab]) {
    return `/admin/academy/settings?tab=${LEGACY_ACADEMY_TAB_MAP[tab]}`;
  }

  if (LEGACY_CLUB_TAB_MAP[tab]) {
    return LEGACY_CLUB_TAB_MAP[tab];
  }

  if (ORGANIZATION_TABS.has(tab)) {
    return `/admin/organization/settings?tab=${tab}`;
  }

  if (ACADEMY_TABS.has(tab)) {
    const normalized =
      tab === 'lessons' || tab === 'lesson-settings'
        ? 'advanced'
        : tab === 'age-groups'
          ? 'general'
          : tab;
    return `/admin/academy/settings?tab=${normalized}`;
  }

  return '/admin/organization/settings';
}

export function parseOrganizationTab(value: string | null): OrganizationSettingsTab {
  if (value && LEGACY_ORGANIZATION_TAB_MAP[value]) {
    return LEGACY_ORGANIZATION_TAB_MAP[value];
  }
  if (value && ORGANIZATION_TABS.has(value)) {
    return value as OrganizationSettingsTab;
  }
  return 'basic';
}

export function parseAcademyTab(value: string | null): AcademySettingsTab {
  if (value && LEGACY_ACADEMY_TAB_MAP[value]) {
    return LEGACY_ACADEMY_TAB_MAP[value];
  }
  if (value === 'general' || value === 'advanced') {
    return value;
  }
  return 'general';
}

/** @deprecated Club settings page removed — use resolveSettingsPath for redirects. */
export function resolveLegacyClubSettingsPath(tab: string | null): string {
  if (tab && LEGACY_CLUB_TAB_MAP[tab]) {
    return LEGACY_CLUB_TAB_MAP[tab];
  }
  return '/admin/court-hire?tab=settings';
}
